<template>
  <div class="mx-auto container container--fluid">
    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
      <span v-if="snackbar_msg">{{ snackbar_msg }}</span>
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-card class="box">
      <v-toolbar dense flat color="white">
        <v-toolbar-title>Map Route to Employee</v-toolbar-title>
      </v-toolbar>

      <!-- Route & Vehicle Selection -->
      <v-row justify="center" dense class="mt-4">
        <v-col sm="3" md="3" cols="10">
          <v-select v-model="selectedRoute" :items="routes" item-text="text" item-value="value" label="Route"
            placeholder="Select a Route" outlined dense @change="onRouteChange" />
        </v-col>
        <v-col sm="3" md="3" cols="10">
          <v-select v-model="selected_vehicle" :items="vehicle_list" item-text="name" item-value="id" outlined dense
            label="Vehicle" :hint="selected_vehicle === 'none' ? 'No vehicles for this route' : ''" persistent-hint />
        </v-col>
        <v-col sm="2" md="2" cols="12">
          <v-btn color="primary" @click="fetchData" :loading="overlay">Fetch</v-btn>
        </v-col>
      </v-row>

      <!-- Search & Actions -->
      <v-row justify="center" dense align="center" class="mt-4">
        <v-col cols="12" sm="6" md="6">
          <v-text-field v-model="search" append-icon="mdi-magnify" label="Search by Employee Code or Name" single-line
            hide-details></v-text-field>
        </v-col>
        <v-col cols="12" sm="3" md="3" class="d-flex justify-center">
          <v-btn color="success" @click="exportData" :loading="overlay" class="mr-4">
            <v-icon small color="white">mdi-export</v-icon>
            <span class="btn-text">Export</span>
          </v-btn>
          <v-btn color="primary" @click="openAddDialog">Add Employee</v-btn>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table :headers="headers" :items="employeeRoutes" class="elevation-1 mytable" :search="search" dense>
        <template v-slot:item.action="{ item }">
          <v-icon small class="mr-2" @click="openEditDialog(item)">mdi-pencil</v-icon>
          <v-icon small @click="deleteItem(item)" color="red">mdi-delete</v-icon>
        </template>
        <template v-slot:item.mappedDate="{ item }">
          {{ item.mappedDate || '-' }}
        </template>
      </v-data-table>

      <!-- Add Dialog -->
      <v-dialog v-model="addDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title><span class="headline">Add Route to Employee</span></v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-select v-model="newRoute" :items="routes.filter(r => r.value !== 'All')" item-text="text"
                    item-value="value" label="Route *" outlined dense
                    @change="fetchVehiclesByRoute(newRoute, false)"></v-select>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="newvehicle" :items="vehicle_list_for_add" item-text="name" item-value="id" outlined
                    dense label="Vehicle *" :hint="newvehicle === 'none' ? 'No vehicles available' : ''"
                    persistent-hint></v-select>
                </v-col>
                <v-col cols="12">
                  <v-autocomplete v-model="selectedEmployees" :items="employees" item-text="employeeName"
                    item-value="employeeCode" label="Select Employees *" multiple chips outlined dense></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeAddDialog">Cancel</v-btn>
            <v-btn color="primary" @click="addEmployeeRoute" :loading="overlay">Add</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Dialog -->
      <v-dialog v-model="editDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title><span class="headline">Edit Employee Route</span></v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete v-model="editEmployee" :items="employees" item-text="employeeName"
                    item-value="employeeCode" label="Employee *" outlined dense 
                    :menu-props="{ closeOnContentClick: true }">
                    <template v-slot:selection="{ item }">
                      <span>{{ item.employeeCode }} - {{ item.employeeName }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <span>{{ item.employeeCode }} - {{ item.employeeName }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="editRoute" :items="routes.filter(r => r.value !== 'All')" item-text="text"
                    item-value="value" label="Route *" outlined dense
                    @change="fetchVehiclesByRoute(editRoute, true)"></v-select>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="editVehicle" :items="vehicle_list_for_edit" item-text="name" item-value="id"
                    outlined dense label="Vehicle *"></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeEditDialog">Cancel</v-btn>
            <v-btn color="primary" @click="editEmployeeRoute" :loading="overlay">Update</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-overlay :value="overlay">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-card>
  </div>
</template>

<script>
  import axios from "axios";

  export default {
    data: () => ({
      snackbar: false,
      snackbar_msg: "",
      color: "",
      search: "",
      overlay: false,

      // Filters
      selectedRoute: "All",
      selected_vehicle: "All",
      routes: [],
      vehicle_list: [{ name: "All", id: "All" }],

      // Table
      employeeRoutes: [],
      headers: [
        { text: "Sr. No.", value: "srNo", sortable: false },
        { text: "Action", value: "action", sortable: false },
        { text: "Employee Code", value: "employeeCode", sortable: true },
        { text: "Employee Name", value: "employeeName", sortable: true },
        { text: "Route No.", value: "routeNo", sortable: true },
        { text: "Vehicle", value: "vehicle", sortable: true },
        { text: "Mapped Date", value: "mappedDate", sortable: true },
        { text: "Mapped By", value: "mappedBy", sortable: true },
      ],

      // Employees
      employees: [],

      // Add Dialog
      addDialog: false,
      newRoute: null,
      newvehicle: null,
      selectedEmployees: [],
      vehicle_list_for_add: [],

      // Edit Dialog
      editDialog: false,
      editItem: null,
      editRoute: null,
      editEmployee: null,
      editVehicle: null,
      vehicle_list_for_edit: [],
      currentVehicle: null,
    }),

    mounted() {
      Promise.all([this.fetchRoutes(), this.fetchEmployees()]).then(() => {
        this.vehicle_list = [{ name: "All", id: "All" }];
        this.selectedRoute = "All";
      });
    },

    methods: {
      // Fetch Routes
      async fetchRoutes() {
        this.overlay = true;
        try {
          const res = await axios.post("/TransportLearnerAttendance/getRoutes", {});
          if (res.data.msg === "200") {
            this.routes = res.data.route_list.map(item => ({ text: item.name, value: item.id }));
            this.routes.unshift({ text: "All", value: "All" });
          }
        } catch (error) {
          this.showSnackbar("#b71c1c", "Failed to load routes");
        } finally {
          this.overlay = false;
        }
      },

      // Fetch Employees
      async fetchEmployees() {
        this.overlay = true;
        try {
          const res = await axios.post("/TransportLearnerAttendance/getEmployeeList", {});
          if (res.data.msg === "200") {
            this.employees = res.data.employee_list.filter(e => e.employeeName && e.employeeName !== e.employeeCode);
          }
        } catch (error) {
          this.showSnackbar("#b71c1c", "Failed to load employees");
        } finally {
          this.overlay = false;
        }
      },

      // Main: Route Change
      onRouteChange(newRoute) {
        this.selected_vehicle = "All";
        this.fetchVehiclesByRoute(newRoute, false);
      },

      // Universal: Fetch Vehicles (Same as Learner)
      async fetchVehiclesByRoute(routeId, isEdit = false) {
        if (!routeId) {
          if (isEdit) {
            this.vehicle_list_for_edit = this.currentVehicle ? [this.currentVehicle] : [];
            this.editVehicle = this.currentVehicle ? this.currentVehicle.id : null;
          } else {
            this.vehicle_list = [{ name: "All", id: "All" }];
            this.selected_vehicle = "All";
            this.vehicle_list_for_add = [];
            this.newvehicle = null;
          }
          return;
        }

        this.overlay = true;
        try {
          const res = await axios.post("/TransportLearnerAttendance/getVehiclesByRoute", { routeId });
          if (res.data.msg === "200") {
            const vehicles = res.data.vehicle_list || [];

            if (isEdit) {
              this.vehicle_list_for_edit = this.currentVehicle && !vehicles.find(v => v.id === this.currentVehicle.id)
                ? [this.currentVehicle, ...vehicles]
                : vehicles;
            } else {
              // Main filter
              if (vehicles.length === 0) {
                this.vehicle_list = [{ name: "No vehicles available", id: "none" }];
                this.selected_vehicle = "none";
              } else {
                this.vehicle_list = [{ name: "All", id: "All" }, ...vehicles.map(v => ({ name: v.name, id: v.id }))];
                this.selected_vehicle = "All";
              }

              // Add dialog
              this.vehicle_list_for_add = vehicles.length === 0
                ? [{ name: "No vehicles available", id: "none" }]
                : vehicles;
              this.newvehicle = vehicles.length === 0 ? "none" : null;
            }

            // Show "No vehicles found" message
            if (vehicles.length === 0 && !isEdit && this.vehicle_list_for_edit.length === 0) {
              this.showSnackbar("#b71c1c", "No vehicles found for this route");
            }
          } else {
            if (isEdit) {
              this.vehicle_list_for_edit = this.currentVehicle ? [this.currentVehicle] : [];
            } else {
              this.vehicle_list = [{ name: "No vehicles available", id: "none" }];
              this.selected_vehicle = "none";
              this.vehicle_list_for_add = [{ name: "No vehicles available", id: "none" }];
              this.newvehicle = "none";
              this.showSnackbar("#b71c1c", res.data.msg || "Failed to fetch vehicles");
            }
          }
        } catch (error) {
          console.error("Error:", error);
          if (isEdit) {
            this.vehicle_list_for_edit = this.currentVehicle ? [this.currentVehicle] : [];
          } else {
            this.vehicle_list = [{ name: "No vehicles available", id: "none" }];
            this.selected_vehicle = "none";
            this.vehicle_list_for_add = [{ name: "No vehicles available", id: "none" }];
            this.newvehicle = "none";
            this.showSnackbar("#b71c1c", "Failed to fetch vehicles");
          }
        } finally {
          this.overlay = false;
        }
      },

      // Fetch Table Data
      async fetchData() {
        if (!this.selectedRoute || this.selectedRoute === "All") {
          this.employeeRoutes = [];
          return;
        }
        if (this.selected_vehicle === "none") {
          this.employeeRoutes = [];
          return;
        }

        this.overlay = true;
        try {
          const payload = {
            selected_route: this.selectedRoute !== "All" ? this.selectedRoute : null,
            vehicle_id: this.selected_vehicle !== "All" ? this.selected_vehicle : null
          };
          const res = await axios.post("/TransportLearnerAttendance/getRouteDetails", payload);
          if (res.data.msg === "200") {
            this.employeeRoutes = res.data.route_details.map((item, index) => ({
              srNo: index + 1,
              id: item.id,
              employeeCode: item.employeeCode,
              employeeName: item.employeeName,
              routeNo: item.routeNo,
              vehicle: item.vehicle || "Not Assigned",
              vehicleId: item.vehicleId || null,
              mappedDate: item.mappedDate ? new Date(item.mappedDate).toLocaleDateString("en-GB") : "-",
              mappedBy: item.mappedBy || "-",
            }));
          } else {
            this.employeeRoutes = [];
          }
        } catch (error) {
          console.error(error);
          this.employeeRoutes = [];
        } finally {
          this.overlay = false;
        }
      },

      // Add Dialog
      openAddDialog() {
        this.addDialog = true;
        this.newRoute = null;
        this.newvehicle = null;
        this.selectedEmployees = [];
        this.vehicle_list_for_add = [];
      },
      closeAddDialog() {
        this.addDialog = false;
        this.newRoute = null;
        this.newvehicle = null;
        this.selectedEmployees = [];
        this.vehicle_list_for_add = [];
      },

      // Add Route to Employees
      async addEmployeeRoute() {
        if (!this.newRoute || !this.newvehicle || this.newvehicle === "none" || !this.selectedEmployees.length) {
          this.showSnackbar("#b71c1c", "Please fill all required fields");
          return;
        }

        this.overlay = true;
        const userId = localStorage.getItem('userId');
        try {
          const res = await axios.post("/TransportLearnerAttendance/addRouteToEmployee", {
            routeNumber: this.newRoute,           // ← ID
            vehicle: this.newvehicle,             // ← ID
            commaSeparatedCodes: this.selectedEmployees,           // ← Array of employeeCode
            userId: userId
          });

          if (res.data.msg === "200") {
            // Re-fetch with same filters
            this.selectedRoute = this.newRoute;
            this.selected_vehicle = this.newvehicle;
            this.fetchData();
            this.closeAddDialog();
            this.showSnackbar("#4caf50", res.data.snackbar || "Added successfully");
          } else {
            this.showSnackbar("#b71c1c", res.data.snackbar || "Failed");
          }
        } catch (error) {
          this.showSnackbar("#b71c1c", "Error adding route");
        } finally {
          this.overlay = false;
        }
      },

      // Edit Dialog
      openEditDialog(item) {
        this.currentVehicle = item.vehicleId ? { id: item.vehicleId, name: item.vehicle } : null;
        this.editItem = { ...item };
        this.editRoute = item.routeNo;
        this.editEmployee = item.employeeCode;
        this.editVehicle = item.vehicleId || null;
        this.vehicle_list_for_edit = this.currentVehicle ? [this.currentVehicle] : [];

        this.$nextTick(() => {
          this.fetchVehiclesByRoute(item.routeNo, true);
        });
        this.editDialog = true;
      },
      closeEditDialog() {
        this.editDialog = false;
        this.editItem = null;
        this.editRoute = null;
        this.editEmployee = null;
        this.editVehicle = null;
        this.currentVehicle = null;
        this.vehicle_list_for_edit = [];
      },

      // Update Route
      async editEmployeeRoute() {
        if (!this.editItem.id || !this.editRoute || !this.editVehicle) {
          this.showSnackbar("#b71c1c", "Please select route and vehicle");
          return;
        }

        this.overlay = true;
        const userId = localStorage.getItem("userId") || "default_user_id";

        try {
          const res = await axios.post("/TransportLearnerAttendance/updateEmployeeRoute", {
            id: this.editItem.id,
            routeId: this.editRoute,
            vehicleId: this.editVehicle,
            userId: userId,
          });

          if (res.data.status === "success") {
            this.fetchData(); // Re-fetch
            this.showSnackbar("#4caf50", "Updated successfully");
            this.closeEditDialog();
          } else {
            this.showSnackbar("#b71c1c", res.data.msg || "Update failed");
          }
        } catch (error) {
          this.showSnackbar("#b71c1c", "Error updating route");
        } finally {
          this.overlay = false;
        }
      },

      // Delete
      deleteItem(item) {
        if (!confirm(`Delete route for ${item.employeeName}?`)) return;
        this.overlay = true;
        axios.post("/TransportLearnerAttendance/deleteEmployeeRoute", { instructorId: item.id })
          .then(() => this.fetchData())
          .catch(() => this.showSnackbar("#b71c1c", "Delete failed"))
          .finally(() => this.overlay = false);
      },

      // Export
      exportData() {
        this.showSnackbar("#4caf50", "Export coming soon!");
      },

      // Snackbar
      showSnackbar(color, msg) {
        this.snackbar = true;
        this.color = color;
        this.snackbar_msg = msg;
      },
    },
  };
</script>

<style scoped>
  .box {
    padding: 20px;
  }

  .mytable {
    margin-top: 20px;
  }

  @media (max-width: 600px) {
    .btn-text {
      display: none;
    }
  }
</style>